import React, { useState } from 'react';

interface PaginatedItemsProps {
    pageCount: number;
    onPageChange: (event: { selected: number }) => void;
    onItemsPerPageChange?: (value: number) => void;
}

const PaginatedItems: React.FC<PaginatedItemsProps> = ({
    pageCount,
    onPageChange,
    onItemsPerPageChange
}) => {
    const [currentPage, setCurrentPage] = useState(0);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        onPageChange({ selected: page });
    };

    return (
        <div className="flex justify-end">
            <div className="flex items-center gap-2">
                <button
                    onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                </button>
                
                <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(4, pageCount) }, (_, i) => {
                        let pageNum;
                        let showEllipsis = false;
                        
                        if (pageCount <= 4) {
                            pageNum = i;
                        } else if (currentPage <= 1) {
                            pageNum = i;
                            showEllipsis = i === 3 && pageCount > 4;
                        } else if (currentPage >= pageCount - 2) {
                            pageNum = pageCount - 4 + i;
                            showEllipsis = i === 0 && pageCount > 4;
                        } else {
                            pageNum = currentPage - 1 + i;
                            showEllipsis = (i === 0 && currentPage > 2) || (i === 3 && currentPage < pageCount - 3);
                        }
                        
                        return (
                            <React.Fragment key={pageNum}>
                                {showEllipsis && (
                                    <span className="px-2 text-gray-500">...</span>
                                )}
                                <button
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`w-8 h-8 p-0 text-sm border rounded-md ${
                                        currentPage === pageNum 
                                            ? 'bg-blue-600 text-white border-blue-600' 
                                            : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNum + 1}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
                
                <button
                    onClick={() => handlePageChange(Math.min(pageCount - 1, currentPage + 1))}
                    disabled={currentPage === pageCount - 1}
                    className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default PaginatedItems; 